import { Button } from "@kaltura/ds-react-components";
import { baseUrl, postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { useState } from "react";
import { createPortal } from "react-dom";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";
import { Delete24Icon } from "@kaltura/ds-react-icons";
import { ActionResponse } from "../types";
import { ConfirmationModal } from "@kaltura/mediaspace-shared-ui";
import { ButtonAnalyticsProvider, useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

/**
 * props for bulk delete component
 */
export interface DeleteAllProps {
    /**
     * callback for refreshing media list after acting
     */
    onReload: () => void;
}

/**
 * my-history delete all component
 */
export const DeleteAll = ({ onReload }: DeleteAllProps) => {
    const [openConfirmation, setOpenConfirmation] = useState(false);
    const [processing, setProcessing] = useState(false);

    const { showToast } = useToastsContext();
    const sendButtonAnalytics = useButtonAnalytics();

    const closeConfirmation = () => {
        sendButtonAnalytics("Cancel Clear History", ButtonClickAnalyticsType.CLOSE, undefined, "My History");
        setOpenConfirmation(false);
    };

    const handleClick = () => {
        setOpenConfirmation(true);
        sendButtonAnalytics("Clear History", ButtonClickAnalyticsType.DELETE, undefined, "My History");
    };

    const handleDelete = async () => {
        setProcessing(true);
        sendButtonAnalytics("Confirm Clear History", ButtonClickAnalyticsType.DELETE, undefined, "My History");
        try {
            const result: ActionResponse = await postKmsData(`${baseUrl}/history-ds/remove-all`);
            if (result.success) {
                const messageText = translate("Your history has been cleared.");
                showToast({ severity: "success", message: messageText });
            }
            else {
                console.error("Remove entry error:", result);
                showToast({ severity: "error", message: translate("Failed to clear your history.") });
            }
        }
        catch (error) {
            console.error("Remove entry error:", error);
            showToast({ severity: "error", message: translate("Failed to clear your history.") });
        }
        setProcessing(false);
        closeConfirmation();
        onReload();
    };

    /**
     * Render modal using Portal to escape Suspense boundary.
     *
     * Problem: This component is rendered inside SearchResultsContainerWithLoadMore's Suspense boundary.
     * When onReload() is called, it triggers data invalidation which activates Suspense, suspending
     * the entire component tree (including this modal). This prevents the modal from closing even though
     * openConfirmation is set to false, because the suspended tree cannot update.
     *
     * Solution: Use createPortal to render the modal directly to document.body, completely outside
     * the Suspense boundary. This allows the modal to close immediately when openConfirmation changes,
     * independent of the Suspense state of its parent components.
     */
    const modalContent = openConfirmation
        ? createPortal(
              <ButtonAnalyticsProvider location={"Clear History Modal"}>
                  <ConfirmationModal
                      yesCallback={handleDelete}
                      yesButtonColor={"danger"}
                      yesButtonLabel={translate("Clear")}
                      open={openConfirmation}
                      noCallback={closeConfirmation}
                      title={translate("Clear your watch history?")}
                      text={translate(
                          "This will remove everything you've watched—across all apps. Are you sure that you want to start fresh? (Heads up: there's no undo.)"
                      )}
                      processing={processing}
                  />
              </ButtonAnalyticsProvider>,
              document.body
          )
        : null;

    return (
        <>
            <Button variant={"borderless"} color={"secondary"} onClick={handleClick} startIcon={<Delete24Icon />}>
                {translate("Clear History")}
            </Button>
            {/* Render the portal content - this is what actually displays the modal */}
            {modalContent}
        </>
    );
};
